import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Award, Clock, Users } from 'lucide-react';
import CTASection from '../components/CTASection';

const AboutPage: React.FC = () => {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-12 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-4 text-center"
          >
            Over HTS Electrotechniek
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-center max-w-3xl mx-auto"
          >
            Meer dan 35 jaar ervaring in elektrotechnische installaties
          </motion.p>
        </div>
      </section>

      {/* Company History */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6 text-navy-800">
                Onze Geschiedenis
              </h2>
              <p className="text-gray-600 mb-4">
                HTS Electrotechniek is opgericht in april 2000 door Halit Suiçmez, die al sinds 1987 
                werkzaam is in de elektrotechnische sector. Met deze jarenlange ervaring besloot 
                Halit zijn eigen bedrijf te starten met een duidelijke visie: het leveren van 
                hoogwaardige elektrotechnische diensten met een persoonlijke aanpak.
              </p>
              <p className="text-gray-600 mb-4">
                Sinds de oprichting heeft HTS Electrotechniek een sterke reputatie opgebouwd als 
                betrouwbare partner voor zowel particuliere als zakelijke klanten in Kampen en 
                omgeving. Door consistent kwaliteit te leveren en altijd beschikbaar te zijn voor 
                onze klanten, is het bedrijf uitgegroeid tot een gevestigde naam in de regio.
              </p>
              <p className="text-gray-600">
                Wat ons onderscheidt is dat alle projecten van begin tot eind worden uitgevoerd door 
                dezelfde persoon. Dit garandeert niet alleen consistentie en kwaliteit, maar zorgt 
                ook voor een vertrouwde relatie met onze klanten. We zijn trots op ons werk en streven 
                ernaar om bij elke opdracht te voldoen aan de hoogste standaarden.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img
                  src="/IMG_9216.jpg"
                  alt="HTS Electrotechniek bedrijfsgebouw met bedrijfsauto"
                  className="rounded-xl shadow-xl w-full object-cover"
                  style={{ aspectRatio: '4/3' }}
                />
                <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white p-4 rounded-lg shadow-lg hidden md:block">
                  <p className="font-bold text-xl">Sinds 2000</p>
                  <p>Meer dan 23 jaar betrouwbare service</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl font-bold mb-4 text-navy-800"
            >
              Onze Missie & Waarden
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-gray-600 max-w-2xl mx-auto"
            >
              We zijn gedreven door een sterke set waarden die ons dagelijks werk vormgeven
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Award size={28} className="text-navy-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-800">Kwaliteit</h3>
              <p className="text-gray-600">
                We streven naar de hoogste kwaliteit in al onze werkzaamheden. Door gebruik te 
                maken van hoogwaardige materialen en ons vakmanschap te perfectioneren, 
                garanderen we duurzame en veilige elektrische installaties.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Users size={28} className="text-navy-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-800">Persoonlijke Service</h3>
              <p className="text-gray-600">
                We geloven in een persoonlijke benadering. Door direct contact met de uitvoerder 
                te hebben, krijgen klanten altijd consistente communicatie en service op maat 
                die aansluit bij hun specifieke behoeften.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Clock size={28} className="text-navy-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-navy-800">Betrouwbaarheid</h3>
              <p className="text-gray-600">
                Onze 24/7 bereikbaarheid is meer dan een service; het is een belofte aan onze 
                klanten. We staan altijd klaar om te helpen, vooral in noodsituaties, en komen 
                onze afspraken altijd na.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-3xl font-bold mb-6 text-navy-800">
                Waarom Kiezen voor HTS Electrotechniek?
              </h2>
              <p className="text-gray-600 mb-6">
                HTS Electrotechniek onderscheidt zich door een unieke combinatie van vakkennis, 
                persoonlijke service en toewijding aan kwaliteit. Onze slogan zegt het al: 
                "Waar de service van een ander stopt, begint onze pas."
              </p>

              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 className="text-green-500 mr-3 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-navy-800">Erkend installatiebedrijf</h3>
                    <p className="text-gray-600">
                      We zijn een volledig erkend elektrotechnisch installatiebedrijf, wat 
                      garandeert dat alle werkzaamheden volgens de geldende normen worden uitgevoerd.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="text-green-500 mr-3 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-navy-800">Één aanspreekpunt</h3>
                    <p className="text-gray-600">
                      Van offerte tot oplevering wordt uw project door dezelfde persoon begeleid, 
                      wat zorgt voor duidelijke communicatie en continuïteit.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="text-green-500 mr-3 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-navy-800">Flexibiliteit</h3>
                    <p className="text-gray-600">
                      We zijn flexibel in onze planning en kunnen snel schakelen, vooral bij 
                      storingen of noodsituaties die onmiddellijke aandacht vereisen.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="text-green-500 mr-3 mt-1" size={20} />
                  <div>
                    <h3 className="font-semibold text-navy-800">Vrijblijvende offertes</h3>
                    <p className="text-gray-600">
                      We bieden altijd duidelijke, vrijblijvende offertes zodat u precies weet 
                      waar u aan toe bent, zonder verborgen kosten of verrassingen.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="/erkend-installateur.jpg"
                  alt="Erkend elektrotechnisch installatiebedrijf certificering"
                  className="rounded-lg shadow-md w-full h-48 object-cover"
                />
                <img
                  src="/persoonlijk-contact.jpg"
                  alt="Persoonlijk contact met de elektricien"
                  className="rounded-lg shadow-md w-full h-48 object-cover mt-8"
                />
                <img
                  src="/24-7-service.jpg"
                  alt="24/7 elektrotechnische service"
                  className="rounded-lg shadow-md w-full h-48 object-cover"
                />
                <img
                  src="/offerte-gesprek.jpg"
                  alt="Vrijblijvend offerte gesprek"
                  className="rounded-lg shadow-md w-full h-48 object-cover mt-8"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default AboutPage;